import { useEffect, useState, useRef } from "react";
import { Camera } from "lucide-react";
import { compressImage } from "@/lib/utils/compress-image";
import { supabase } from "@/lib/supabase";
import { StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "@/dentlab/src/store/Central/selectors";

interface ImageInputProps {
	bucket?: StorageBucketsEnum;
	onFileChange: (file: File | null) => void;
	avatarPath?: string;
	organizationId?: string;
	initials?: string;
	size?: "sm" | "md" | "lg";
	onPreviewChange?: (previewUrl: string | null) => void;
}

/**
 * USES ORG_V1 AS BUCKET, i.e. DO NOT USE for images that should be share-able.
 * Only use in components such as client.form.tsx where usage of the file is only internal org-wide.
 */
const SIZE_CLASSES = {
	sm: "w-12 h-12",
	md: "w-16 h-16",
	lg: "w-20 h-20",
};

export const ImageInput: React.FC<ImageInputProps> = ({
	bucket,
	avatarPath,
	onFileChange,
	organizationId,
	initials,
	size = "md",
	onPreviewChange,
}) => {
	const [profilePic, setProfilePic] = useState<string>("");
	const fileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!bucket || !organizationId) return;

		const fetchProfilePic = async () => {
			if (!avatarPath || avatarPath?.length <= 0) {
				Logger.log("[fetchProfilePic] No avatar path provided");
				return;
			}
			const { data, error } = await supabase.storage
				.from(bucket)
				.createSignedUrl(`${organizationId}/${avatarPath}`, 3600, {
					transform: {
						quality: 35,
					},
				});
			if (error) {
				Logger.log(
					"[fetchProfilePic] Error fetching signed URL",
					error
				);
				return;
			}
			if (!data) {
				Logger.log(
					"[fetchProfilePic] No data returned from signed URL"
				);
				return;
			}
			setProfilePic(data.signedUrl);
			onPreviewChange?.(data.signedUrl);
		};

		fetchProfilePic();
	}, [avatarPath, organizationId, bucket]);

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files || e.target.files.length <= 0) return;
		const file = e.target.files[0];
		if (file.size > 5 * 1024 * 1024) {
			showNotification({
				message: "Bitte wählen Sie eine Bilddatei unter 5MB.",
				type: "warning",
			});
			return;
		}
		if (!file) {
			setProfilePic("");
			showNotification({
				message: "Fehler beim Hochladen des Avatars",
				type: "error",
			});
			return;
		}

		const compressedFile = await compressImage(file, 0.6);

		onFileChange(compressedFile);
		const reader = new FileReader();
		reader.onloadend = () => {
			const previewUrl = reader.result as string;
			setProfilePic(previewUrl);
			onPreviewChange?.(previewUrl);
		};
		reader.readAsDataURL(file);
	};

	return (
		<div
			className={`relative ${SIZE_CLASSES[size]} rounded-md bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer group`}
			onClick={() => fileInputRef.current?.click()}
		>
			{profilePic ? (
				<img
					src={profilePic}
					className="w-full h-full object-cover"
					alt="Profilbild"
				/>
			) : (
				<span className="text-xl font-semibold text-gray-400">
					{initials || <Camera className="w-6 h-6" />}
				</span>
			)}
			<div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
				<Camera className="w-5 h-5 text-white" />
			</div>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				className="hidden"
				onChange={handleFileChange}
				aria-label="Profilbild hochladen"
				data-testid="image-input"
			/>
		</div>
	);
};
